import React, { useEffect, useState } from 'react';
import API from 'shared/api';
import { FiLock } from 'react-icons/fi';
import { COL } from 'shared/darkColors';
import {
  S, money, intFmt, Kpi, SectionCard, Cargando, ErrorMsg, buildQuery, descargarCSV,
} from './reportesCommon';

export default function ReporteCierres({ filtros }) {
  const [cierres, setCierres] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [ultimoId, setUltimoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const cargar = async (reset) => {
    setLoading(true);
    setError(false);
    try {
      const p = buildQuery(filtros);
      p.set('limite', '50');
      if (!reset && ultimoId) p.set('ultimo_id', ultimoId);
      const r = await API.get(`/cierre-caja/listar?${p.toString()}`);
      if (r.data.status === 'success') {
        setCierres((prev) => (reset ? r.data.data : [...prev, ...r.data.data]));
        setHasMore(r.data.has_more);
        if (r.data.has_more && r.data.data.length) setUltimoId(r.data.data[r.data.data.length - 1].id);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCierres([]);
    setUltimoId(null);
    setHasMore(false);
    cargar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.sede, filtros.fechaDesde, filtros.fechaHasta]);

  const abrirDetalle = async (id) => {
    try {
      const r = await API.get(`/cierre-caja/${id}`);
      if (r.data.status === 'success') setDetalle(r.data.data);
    } catch {}
  };

  const exportar = () => {
    descargarCSV(
      'reporte_cierres.csv',
      ['Fecha apertura', 'Sede', 'Usuario', 'Estado', 'Efectivo', 'Tarjeta', 'Transferencia', 'Otros', 'Diferencia'],
      cierres.map((c) => [
        c.fecha_apertura, c.sede_codigo, c.usuario_nombre || c.usuario_cedula, c.estado,
        c.total_efectivo, c.total_tarjeta, c.total_transferencia, c.total_otros, c.diferencia_total,
      ])
    );
  };

  if (loading && cierres.length === 0) return <Cargando />;
  if (error && cierres.length === 0) return <ErrorMsg />;

  const cerrados = cierres.filter((c) => c.estado === 'cerrado');
  const abiertos = cierres.filter((c) => c.estado === 'abierto');
  const difTotal = cerrados.reduce((s, c) => s + Number(c.diferencia_total || 0), 0);

  return (
    <div>
      <div style={S.kpiGrid}>
        <Kpi label="Cierres" value={intFmt(cierres.length)} color={COL.pri} />
        <Kpi label="Cerrados" value={intFmt(cerrados.length)} color={COL.green} />
        <Kpi label="Abiertos" value={intFmt(abiertos.length)} color={COL.amber} />
        <Kpi label="Diferencia acumulada" value={money(difTotal)} color={Math.abs(difTotal) > 0 ? COL.red : COL.green} />
      </div>

      <SectionCard title="Cierres de caja" icon={FiLock} color={COL.pri} hint="Arqueo por sede y cajero">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <button onClick={exportar} style={S.btn}>Exportar CSV</button>
        </div>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>{['Apertura', 'Sede', 'Usuario', 'Estado', 'Efectivo', 'Tarjeta', 'Transferencia', 'Otros', 'Diferencia'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {cierres.map((c) => (
                <tr key={c.id} onClick={() => abrirDetalle(c.id)} style={{ cursor: 'pointer' }}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{c.fecha_apertura?.slice(0, 16).replace('T', ' ')}</td>
                  <td style={{ ...S.td, color: COL.muted }}>{c.sede_codigo}</td>
                  <td style={S.td}>{c.usuario_nombre || c.usuario_cedula || '—'}</td>
                  <td style={S.td}>
                    <span style={{ ...S.chip, color: c.estado === 'abierto' ? COL.amber : COL.green, background: COL.bg }}>
                      {c.estado === 'abierto' ? 'Abierto' : 'Cerrado'}
                    </span>
                  </td>
                  <td style={S.td}>{money(c.total_efectivo)}</td>
                  <td style={S.td}>{money(c.total_tarjeta)}</td>
                  <td style={S.td}>{money(c.total_transferencia)}</td>
                  <td style={S.td}>{money(c.total_otros)}</td>
                  <td style={{ ...S.td, fontWeight: 700, color: Number(c.diferencia_total) !== 0 ? COL.red : COL.muted }}>
                    {money(c.diferencia_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cierres.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin cierres en el período</div>}
        {hasMore && (
          <div style={{ textAlign: 'center', marginTop: '10px' }}>
            <button onClick={() => cargar(false)} disabled={loading} style={S.btn}>
              {loading ? 'Cargando…' : 'Cargar más'}
            </button>
          </div>
        )}
      </SectionCard>

      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}
          onClick={() => setDetalle(null)}>
          <div style={{ background: COL.white, borderRadius: '12px', maxWidth: '520px', width: '100%', maxHeight: '80vh', overflowY: 'auto', padding: '16px', boxShadow: '0 12px 32px rgba(16,24,40,0.25)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
              <div style={{ fontSize: '15px', fontWeight: 800, color: COL.pri }}>Detalle de cierre #{detalle.id}</div>
              <button onClick={() => setDetalle(null)} style={{ ...S.btn, padding: '4px 8px' }}>Cerrar</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '8px', fontSize: '12px' }}>
              {[
                ['Base inicial $', detalle.base_inicial_usd],
                ['Efectivo', detalle.total_efectivo],
                ['Tarjeta', detalle.total_tarjeta],
                ['Transferencia', detalle.total_transferencia],
                ['Otros', detalle.total_otros],
                ['Pedidos tienda', detalle.total_pedidos_tienda],
                ['Delivery', detalle.total_delivery],
                ['Anulados', detalle.total_anulados],
                ['Diferencia', detalle.diferencia_total],
              ].map(([l, v]) => (
                <div key={l} style={{ background: COL.bg, borderRadius: '8px', padding: '8px 10px' }}>
                  <div style={{ color: COL.muted, fontSize: '10px', fontWeight: 700 }}>{l}</div>
                  <div style={{ fontSize: '14px', fontWeight: 800 }}>{money(v)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
